import { useEffect, useMemo, useState } from "react";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import DropDownSearch from "@components/ui/dropdown/DropDownSearch";
import DatePicker from "@components/ui/selection/DatePicker";
import TimePicker from "@components/ui/selection/TimePicker";
import Icon1 from "@assets/icons/Icon";
import type { Vehicle, VehicleReservation } from "types/garage/vehicules";
import { hourToTimeStr, timeStrToHour } from "@utils/timeGrid";
import { useDataStore } from "@store/user_org";
import { vehiclesApi } from "@services/api/vehicles";
import { showNotification } from "context/notification.service";

function isoToSeconds(iso: string): number | null {
  if (!iso) return null;
  return Math.floor(new Date(iso + "T12:00:00").getTime() / 1000);
}

function secondsToIso(seconds: number): string {
  const d = new Date(seconds * 1000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function todayIso(): string {
  return secondsToIso(Math.floor(Date.now() / 1000));
}

/**
 * Changer de véhicule en cours de mobilité (« bascule ») : le véhicule actuel rentre à la
 * date / heure choisie, le nouveau part de là jusqu'à la fin prévue, sur le même ordre.
 * Jérôme, 2026-10-14 (BAUJARD rend l'Astra et repart en 208) : « il faut pouvoir arrêter
 * 1 véhicule pour lui en attribuer un nouveau sur le même ordre ». Le serveur écrit aussi
 * la bascule dans ServiceBox (second prêt sur le même RDV).
 */
export default function SwapVehicleModal({
  reservation,
  vehicles,
  onClose,
  onSwapped,
}: {
  reservation: VehicleReservation;
  vehicles: Vehicle[];
  onClose: () => void;
  onSwapped: (pair: VehicleReservation[]) => void;
}) {
  const orgId = useDataStore((s) => s.orgData?.id);
  const today = todayIso();
  const initialDay =
    today > reservation.startDate && today <= reservation.endDate ? today : reservation.startDate;
  const [day, setDay] = useState(initialDay);
  const [returnHour, setReturnHour] = useState<number>(17);
  const [departHour, setDepartHour] = useState<number>(17);
  const [vehicleId, setVehicleId] = useState("");
  const [saving, setSaving] = useState(false);
  const [availableIds, setAvailableIds] = useState<Set<string> | null>(null);

  const endHour = reservation.returnHour ?? undefined;
  useEffect(() => {
    if (!orgId || !day) return;
    let alive = true;
    setAvailableIds(null);
    vehiclesApi
      .available({
        org_id: orgId,
        start_date: day,
        end_date: reservation.endDate,
        start_hour: departHour,
        end_hour: endHour,
        all_categories: true,
      })
      .then((list) => { if (alive) setAvailableIds(new Set(list.map((v) => v.id))); })
      .catch(() => { if (alive) setAvailableIds(null); });
    return () => { alive = false; };
  }, [orgId, day, departHour, endHour, reservation.endDate]);

  const options = useMemo(
    () =>
      vehicles
        .filter((v) => v.id !== reservation.vehicleId)
        .filter((v) => availableIds == null || availableIds.has(v.id))
        .map((v) => ({ value: v.id, label: `${v.name} — ${v.plate}` })),
    [vehicles, availableIds, reservation.vehicleId],
  );
  const current = vehicles.find((v) => v.id === reservation.vehicleId);

  const submit = async () => {
    if (!vehicleId) {
      showNotification({ type: "error", message: "Choisissez le nouveau véhicule" });
      return;
    }
    if (departHour < returnHour) {
      showNotification({ type: "error", message: "Le nouveau véhicule part après le retour de l'actuel" });
      return;
    }
    setSaving(true);
    try {
      const pair = await vehiclesApi.swapReservation(reservation.id, {
        vehicle_id: vehicleId,
        end_date: day,
        end_hour: returnHour,
        start_date: day,
        start_hour: departHour,
      });
      showNotification({ type: "success", message: "Véhicule changé" });
      onSwapped(pair);
    } catch (e: any) {
      showNotification({
        type: "error",
        message: e?.response?.data?.detail || "Changement de véhicule impossible",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseModal
      title="Changer de véhicule"
      startIcon={<Icon1 name="modify" size={20} />}
      onClose={onClose}
      width="max-w-xl"
    >
      <div className="flex flex-col gap-3 p-4">
        <span className="text-h3 text-neutral-500">
          {current ? `${current.name} — ${current.plate}` : "Le véhicule actuel"} rentre à la date
          choisie ; le nouveau part ensuite jusqu'à la fin prévue
          ({reservation.endDate.split("-").reverse().join("/")}), sur le même ordre.
        </span>
        <DatePicker
          label="Date du changement"
          seconds={isoToSeconds(day)}
          onChange={(s) => setDay(secondsToIso(s))}
          minDateSeconds={isoToSeconds(reservation.startDate)}
        />
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <TimePicker
              label="Retour du véhicule actuel"
              value={hourToTimeStr(returnHour)}
              onChange={(t) => {
                if (!t) return;
                const h = timeStrToHour(t);
                setReturnHour(h);
                if (departHour < h) setDepartHour(h);
              }}
            />
          </div>
          <div className="flex-1">
            <TimePicker
              label="Départ du nouveau"
              value={hourToTimeStr(departHour)}
              onChange={(t) => { if (t) setDepartHour(timeStrToHour(t)); }}
            />
          </div>
        </div>
        <DropDownSearch
          label="Nouveau véhicule"
          options={options}
          value={vehicleId}
          onSelect={(v) => setVehicleId(v as string)}
        />
        <span className="text-h4 text-neutral-400 -mt-1">
          {availableIds == null
            ? "Recherche des véhicules disponibles…"
            : "Seuls les véhicules libres du changement à la fin sont proposés."}
        </span>
      </div>
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>Annuler</SecondaryButton>
        <Button variant="small" label="Changer de véhicule" onClick={submit} disabled={saving} />
      </div>
    </BaseModal>
  );
}
